import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUploadCloud, FiFileText, FiClipboard, FiDownload, FiLoader, FiAlertTriangle, FiSave } from 'react-icons/fi';
import SectionTitle from '@/components/SectionTitle';
import logger from '@/utils/logger';
import dropboxClient from '@/utils/dropboxClient';
import Accordion from '@/components/Accordion';

const FilePreview = ({ path, content }) => {
  return (
    <div className="border border-gray-200 rounded-md mb-2">
      <Accordion title={
        <div className="flex items-center text-sm">
          <FiFileText className="mr-2 text-gray-500" />
          <span>{path}</span>
        </div>
      }>
        <pre className="bg-gray-800 text-white p-4 rounded-b-md text-xs overflow-auto max-h-64">
          <code>{content}</code>
        </pre>
      </Accordion>
    </div>
  );
};

function CodeExtractor() {
  const [zipFile, setZipFile] = useState(null);
  const [extractedFiles, setExtractedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [isUploadingToDropbox, setIsUploadingToDropbox] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, []);
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = (file) => {
    if (!file.type.includes('zip') && !file.name.endsWith('.zip')) {
      setError('Invalid file type. Please upload a .zip file.');
      return;
    }
    setError('');
    setZipFile(file);
    setExtractedFiles([]);
    
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      setIsLoading(true);
      try {
        const base64String = reader.result.split(',')[1];
        const response = await fetch('/api/unzip/unzip', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ zipBase64: base64String }),
        });
        
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to extract files.');
        
        setExtractedFiles(data.files);
        logger.success(`Extracted ${data.files.length} files from ${file.name}`);
      } catch (err) {
        setError(err.message);
        logger.error('File extraction failed', { error: err.message });
      } finally {
        setIsLoading(false);
      }
    };
    reader.onerror = () => {
      setError('Failed to read file.');
      setIsLoading(false);
    };
  };
  
  const buildConsolidatedText = () => {
    return extractedFiles
      .map(file => `// --- ${file.path} ---\n\n${file.content}\n\n`)
      .join('');
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(buildConsolidatedText());
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };
  
  const handleDownloadTxt = () => {
    const text = buildConsolidatedText();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${zipFile.name.replace(/\.zip$/, '')}-code.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleUploadToDropbox = async () => {
    setIsUploadingToDropbox(true);
    try {
        const text = buildConsolidatedText();
        const base64Data = btoa(unescape(encodeURIComponent(text)));
        const fileName = `${zipFile.name.replace(/\.zip$/, '')}-code.txt`;
        await dropboxClient.uploadFile(`/promptly_exports/${fileName}`, base64Data);
        logger.success(`Successfully uploaded consolidated code to Dropbox as ${fileName}`);
    } catch(err) {
        logger.error('Failed to upload to Dropbox', { error: err.message });
    } finally {
        setIsUploadingToDropbox(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <SectionTitle title="Code Extractor Tool" subtitle="Upload a .zip file to extract and view all code in a single place." />
      
      <div className="bg-white p-8 rounded-xl shadow-lg">
        <label
          onDragOver={handleDragOver} onDrop={handleDrop} htmlFor="zip-upload"
          className="relative block w-full border-2 border-gray-300 border-dashed rounded-lg p-12 text-center hover:border-gray-400 cursor-pointer"
        >
          <FiUploadCloud className="mx-auto h-12 w-12 text-gray-400" />
          <span className="mt-2 block text-sm font-medium text-gray-900">{zipFile ? zipFile.name : 'Upload a .zip file'}</span>
          <span className="block text-xs text-gray-500">or drag and drop</span>
          <input id="zip-upload" type="file" accept=".zip,application/zip" className="sr-only" onChange={handleFileChange} />
        </label>
        
        <AnimatePresence>
          {isLoading && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-6">
              <FiLoader className="mx-auto h-8 w-8 text-indigo-600 animate-spin" />
              <p className="mt-2 text-sm text-gray-600">Extracting files...</p>
            </motion.div>
          )}
        </AnimatePresence>
        
        {error && (
          <motion.div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex"><div className="flex-shrink-0"><FiAlertTriangle className="h-5 w-5 text-red-400" /></div><div className="ml-3"><p className="text-sm text-red-700">{error}</p></div></div>
          </motion.div>
        )}
        
        {extractedFiles.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8">
            <h3 className="text-lg font-medium text-gray-900">Extracted {extractedFiles.length} files</h3>
            <div className="flex flex-wrap gap-3 my-4">
              <button onClick={handleCopyAll} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"><FiClipboard className="mr-2" /> {copySuccess ? 'Copied!' : 'Copy All Code'}</button>
              <button onClick={handleDownloadTxt} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"><FiDownload className="mr-2" /> Download .txt</button>
              <button onClick={handleUploadToDropbox} disabled={isUploadingToDropbox} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                {isUploadingToDropbox ? <FiLoader className="animate-spin mr-2" /> : <FiSave className="mr-2" />}
                {isUploadingToDropbox ? 'Uploading...' : 'Upload .txt to Dropbox'}
              </button>
            </div>
            <div className="mt-4 max-h-[50vh] overflow-y-auto pr-2">{extractedFiles.map(file => <FilePreview key={file.path} {...file} />)}</div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default CodeExtractor;